import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../../contexts/ToastContext';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { apiService } from '../../../../services/api';
import styles from './NotificationPreferences.module.css';

interface PreferenceRow {
    notificationType: number;
    typeName: string;
    emailEnabled: boolean;
    inAppEnabled: boolean;
    pushEnabled: boolean;
}

// O3 (SO-38) — plain-language names + one-line descriptions for every
// notification type the API returns (BE `NotificationType` enum names). Every
// type stays visible; unknown future types fall back to a humanized name.
const NOTIFICATION_TYPE_KEYS: Record<string, string> = {
    NewAppointment: 'onot.type.newAppointment',
    AppointmentCancelled: 'onot.type.appointmentCancelled',
    AppointmentCompleted: 'onot.type.appointmentCompleted',
    NewStaffJoined: 'onot.type.newStaffJoined',
    PromotionExpiring: 'onot.type.promotionExpiring',
    System: 'onot.type.system',
    NewBusinessCreated: 'onot.type.newBusinessCreated',
    NewUserRegistered: 'onot.type.newUserRegistered',
    OnboardingApproved: 'onot.type.onboardingApproved',
    OnboardingRejected: 'onot.type.onboardingRejected',
    OnboardingSubmitted: 'onot.type.onboardingSubmitted',
    BusinessSuspended: 'onot.type.businessSuspended',
    BusinessReinstated: 'onot.type.businessReinstated',
};

/** "BookingCreatedNotification" -> "Booking created" (fallback for unknown types). */
export function humanizeTypeName(raw: string): string {
    const words = (raw || '')
        .replace(/Notification$/, '')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .trim()
        .toLowerCase();
    return words ? words.charAt(0).toUpperCase() + words.slice(1) : raw;
}

const NotificationPreferences: React.FC = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const { t } = useLanguage();
    const typeLabel = (typeName: string): string => {
        const k = NOTIFICATION_TYPE_KEYS[typeName];
        return k ? t(`${k}.name`) : humanizeTypeName(typeName);
    };
    const typeDescription = (typeName: string): string | null => {
        const k = NOTIFICATION_TYPE_KEYS[typeName];
        return k ? t(`${k}.desc`) : null;
    };
    const [preferences, setPreferences] = useState<PreferenceRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [pushSupported, setPushSupported] = useState(false);
    const [pushSubscribed, setPushSubscribed] = useState(false);

    const fetchPreferences = useCallback(async () => {
        try {
            setLoading(true);
            const response = await apiService.getNotificationPreferences();
            setPreferences(response.preferences || []);
        } catch {
            showToast(t('onot.toast.loadFailed'), 'error');
        } finally {
            setLoading(false);
        }
    }, [showToast, t]);

    useEffect(() => {
        fetchPreferences();
        checkPushSupport();
    }, [fetchPreferences]);

    const checkPushSupport = async () => {
        if ('serviceWorker' in navigator && 'PushManager' in window) {
            setPushSupported(true);
            try {
                const registration = await navigator.serviceWorker.getRegistration('/sw-push.js');
                if (registration) {
                    const subscription = await registration.pushManager.getSubscription();
                    setPushSubscribed(!!subscription);
                }
            } catch {
                // Push check failed silently
            }
        }
    };

    const handleToggle = (index: number, channel: 'emailEnabled' | 'inAppEnabled' | 'pushEnabled') => {
        setPreferences(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [channel]: !updated[index][channel] };
            return updated;
        });
        setDirty(true);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const payload = preferences.map(p => ({
                notificationType: p.notificationType,
                emailEnabled: p.emailEnabled,
                inAppEnabled: p.inAppEnabled,
                pushEnabled: p.pushEnabled,
            }));
            await apiService.updateNotificationPreferences(payload);
            showToast(t('onot.toast.saved'), 'success');
            setDirty(false);
        } catch {
            showToast(t('onot.toast.saveFailed'), 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleEnablePush = async () => {
        try {
            // Register service worker
            const registration = await navigator.serviceWorker.register('/sw-push.js');
            await navigator.serviceWorker.ready;

            // Request permission
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                showToast(t('onot.push.denied'), 'error');
                return;
            }

            // Subscribe to push
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: process.env.REACT_APP_VAPID_PUBLIC_KEY
            });

            const p256dh = subscription.getKey('p256dh');
            const auth = subscription.getKey('auth');

            if (p256dh && auth) {
                await apiService.subscribeWebPush({
                    endpoint: subscription.endpoint,
                    p256dhKey: btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(p256dh)))),
                    authKey: btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(auth))))
                });
                setPushSubscribed(true);
                showToast(t('onot.push.enabledToast'), 'success');
            }
        } catch (err) {
            showToast(t('onot.push.enableFailed'), 'error');
        }
    };

    const handleDisablePush = async () => {
        try {
            const registration = await navigator.serviceWorker.getRegistration('/sw-push.js');
            if (registration) {
                const subscription = await registration.pushManager.getSubscription();
                if (subscription) {
                    await apiService.unsubscribeWebPush(subscription.endpoint);
                    await subscription.unsubscribe();
                }
            }
            setPushSubscribed(false);
            showToast(t('onot.push.disabledToast'), 'success');
        } catch {
            showToast(t('onot.push.disableFailed'), 'error');
        }
    };

    if (loading) {
        return <div className={styles.loading}>{t('onot.loading')}</div>;
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <button
                    type="button"
                    className={styles.backButton}
                    onClick={() => navigate('/settings')}
                    aria-label={t('oset.backToSettings')}
                >
                    <svg aria-hidden="true" focusable="false" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                </button>
                <h2 className="xH1">{t('settings.card.notifications.title')}</h2>
            </div>

            {pushSupported && (
                <div className={styles.pushSection}>
                    <h3>{t('onot.push.sectionTitle')}</h3>
                    <div className={styles.pushRow}>
                        <div className={styles.pushInfo}>
                            <h4>{t('onot.push.title')}</h4>
                            <p>{t('onot.push.desc')}</p>
                        </div>
                        {pushSubscribed ? (
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                <span className={`${styles.pushStatus} ${styles.pushEnabled}`}>{t('onot.push.enabled')}</span>
                                <button type="button" className={styles.pushButton} onClick={handleDisablePush} style={{ background: 'var(--destructive-fill)' }}>
                                    {t('onot.push.disable')}
                                </button>
                            </div>
                        ) : (
                            <button type="button" className={styles.pushButton} onClick={handleEnablePush}>
                                {t('onot.push.enable')}
                            </button>
                        )}
                    </div>
                </div>
            )}

            <div className={styles.card}>
                <div className={styles.tableHeader}>
                    <span>{t('onot.col.type')}</span>
                    <span>{t('onot.col.email')}</span>
                    <span>{t('onot.col.inApp')}</span>
                    <span>{t('onot.col.push')}</span>
                </div>
                {preferences.map((pref, index) => {
                    const label = typeLabel(pref.typeName);
                    const description = typeDescription(pref.typeName);
                    return (
                    <div key={pref.notificationType} className={styles.prefRow}>
                        <span className={styles.prefLabel}>
                            <span className={styles.prefName}>{label}</span>
                            {description && <span className={styles.prefDesc}>{description}</span>}
                        </span>
                        <div className={styles.toggleCell}>
                            <input
                                type="checkbox"
                                className={styles.toggle}
                                checked={pref.emailEnabled}
                                onChange={() => handleToggle(index, 'emailEnabled')}
                                aria-label={`${label} — ${t('onot.col.email')}`}
                            />
                        </div>
                        <div className={styles.toggleCell}>
                            <input
                                type="checkbox"
                                className={styles.toggle}
                                checked={pref.inAppEnabled}
                                onChange={() => handleToggle(index, 'inAppEnabled')}
                                aria-label={`${label} — ${t('onot.col.inApp')}`}
                            />
                        </div>
                        <div className={styles.toggleCell}>
                            <input
                                type="checkbox"
                                className={styles.toggle}
                                checked={pref.pushEnabled}
                                onChange={() => handleToggle(index, 'pushEnabled')}
                                aria-label={`${label} — ${t('onot.col.push')}`}
                            />
                        </div>
                    </div>
                    );
                })}
            </div>

            <div className={styles.saveBar}>
                <button
                    type="button"
                    className={styles.saveButton}
                    onClick={handleSave}
                    disabled={!dirty || saving}
                >
                    {saving ? t('staff.saving') : t('onot.save')}
                </button>
            </div>
        </div>
    );
};

export default NotificationPreferences;
